import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatBRL } from "@/lib/api";
import { TrendingUp, Users, Building2, Stethoscope, AlertCircle, BadgePercent, ArrowRight, Gift } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";

export default function GestorDashboard() {
  const [k, setK] = useState(null);

  useEffect(() => { api.get("/dashboard/kpis").then(r=>setK(r.data)).catch(()=>{}); }, []);

  if (!k) return <div className="text-center py-20 text-slate-500" data-testid="gestor-loading">Carregando KPIs...</div>;

  const kpis = [
    { label: "Faturamento bruto", value: formatBRL(k.faturamento_bruto), icon: TrendingUp, color: "#0E2A5C", testid: "kpi-bruto" },
    { label: "Faturamento líquido", value: formatBRL(k.faturamento_liquido), icon: BadgePercent, color: "#2A8FE0", testid: "kpi-liquido" },
    { label: "Inadimplência", value: formatBRL(k.inadimplencia), icon: AlertCircle, color: "#EF4444", testid: "kpi-inadimplencia" },
    { label: "Contratos ativos", value: k.contratos_ativos, icon: Users, color: "#10B981", testid: "kpi-contratos" },
    { label: "Clientes totais", value: k.total_clientes, icon: Users, color: "#0E2A5C", testid: "kpi-clientes-total" },
    { label: "Credenciados", value: k.total_credenciados, icon: Stethoscope, color: "#2A8FE0", testid: "kpi-credenciados-total" },
    { label: "Empresas B2B", value: k.total_empresas, icon: Building2, color: "#F59E0B", testid: "kpi-empresas-total" },
    { label: "Atendimentos", value: k.atendimentos_count, icon: Stethoscope, color: "#0EA5E9", testid: "kpi-atendimentos-total" },
  ];

  const referralKpis = [
    { label: "Indicações totais", value: k.indicacoes_total ?? 0, icon: Gift, color: "#0E2A5C", testid: "kpi-indicacoes-total" },
    { label: "Indicações convertidas", value: k.indicacoes_creditadas ?? 0, icon: Users, color: "#10B981", testid: "kpi-indicacoes-creditadas" },
    { label: "Cashback pago", value: formatBRL(k.cashback_pago ?? 0), icon: BadgePercent, color: "#2A8FE0", testid: "kpi-cashback-pago" },
    { label: "Saldo em circulação", value: formatBRL(k.cashback_em_circulacao ?? 0), icon: TrendingUp, color: "#F59E0B", testid: "kpi-cashback-saldo" },
  ];

  return (
    <div className="space-y-8" data-testid="gestor-dashboard">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {kpis.map(it => (
          <div key={it.label} className="kpi-tile" data-testid={it.testid}>
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{it.label}</div>
              <it.icon className="h-4 w-4" style={{color: it.color}} />
            </div>
            <div className="font-display text-3xl font-black" style={{color: it.color}}>{it.value}</div>
          </div>
        ))}
      </div>

      {/* Programa de Indicação */}
      <div className="card-soft p-6" data-testid="referral-program-section">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-xl font-bold text-[#0E2A5C] flex items-center gap-2">
              <Gift className="h-5 w-5 text-[#2A8FE0]" /> Programa de Indicação
            </h2>
            <p className="text-sm text-slate-500">Cada cliente indica amigos e ganha 10% de cashback na primeira fatura paga</p>
          </div>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {referralKpis.map(it => (
            <div key={it.label} className="rounded-2xl border border-slate-100 p-5" data-testid={it.testid}>
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{it.label}</div>
                <it.icon className="h-4 w-4" style={{color: it.color}} />
              </div>
              <div className="font-display text-2xl font-black mt-1" style={{color: it.color}}>{it.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-soft p-6" data-testid="chart-bruto-liquido">
          <h2 className="font-display text-xl font-bold text-[#0E2A5C]">Faturamento bruto vs líquido</h2>
          <p className="text-sm text-slate-500">Últimos 6 meses</p>
          <div className="mt-5 h-72">
            <ResponsiveContainer>
              <LineChart data={k.monthly_series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v)=>`R$${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v)=>formatBRL(v)} />
                <Legend />
                <Line type="monotone" dataKey="bruto" stroke="#0E2A5C" strokeWidth={3} dot={{r:5}} name="Bruto" />
                <Line type="monotone" dataKey="liquido" stroke="#2A8FE0" strokeWidth={3} dot={{r:5}} name="Líquido" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-soft p-6" data-testid="chart-status">
          <h2 className="font-display text-xl font-bold text-[#0E2A5C]">Status de cobrança</h2>
          <p className="text-sm text-slate-500">Distribuição por situação</p>
          <div className="mt-5 h-72">
            <ResponsiveContainer>
              <BarChart data={k.invoices_breakdown.map(b => ({ status: b._id, total: b.total, count: b.count }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="status" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip formatter={(v)=>formatBRL(v)} />
                <Bar dataKey="total" fill="#2A8FE0" radius={[8,8,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {[
          { title: "Gerenciar Rede Credenciada", desc: "Cadastre clínicas, especialistas e parceiros", to: "/painel/gestor/rede", testid: "card-rede" },
          { title: "Empresas B2B", desc: "Gestão de vidas e faturamento por CNPJ", to: "/painel/gestor/empresas", testid: "card-empresas" },
          { title: "Relatórios", desc: "Exportar XLSX, PDF e CSV", to: "/painel/gestor/relatorios", testid: "card-relatorios" },
        ].map(c => (
          <Link key={c.to} to={c.to} className="card-soft p-6 group" data-testid={c.testid}>
            <h3 className="font-display text-lg font-bold text-[#0E2A5C]">{c.title}</h3>
            <p className="text-sm text-slate-500 mt-1">{c.desc}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#2A8FE0]">Acessar <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition" /></span>
          </Link>
        ))}
      </div>
    </div>
  );
}
